import {useApolloClient, useQuery} from '@shopify/react-graphql';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {useLocalStorage} from '@web-utilities/local-storage';
import {LOCALE_STORAGE_KEY} from 'features/Translations/constants';
import {useUrlParams} from 'utilities/use-url-params';
import {
  Market,
  ShopLocale,
  ShopLocalesAndMarketsContext,
} from 'utilities/use-shop-locales-and-markets';

import ShopLocalesAndMarketsQuery from './graphql/ShopLocalesAndMarketsQuery.graphql';
import MarketDetailsQuery, {
  MarketDetailsQueryData,
} from './graphql/MarketDetailsQuery.graphql';
import {marketHandleFromLocaleChange} from './utils';

export function ShopLocalesAndMarkets({children}: {children: ReactNode}) {
  const apollo = useApolloClient();
  const [{value: currentLocaleInStorage, update: storeCurrentLocale}] =
    useLocalStorage<ShopLocale>(LOCALE_STORAGE_KEY);
  const {urlParams, setUrlParams} = useUrlParams();
  const [initialized, setInitialized] = useState(false);

  const initialLocale = useRef<ShopLocale | undefined>(
    (() => {
      if (urlParams.shopLocale) {
        return {
          locale: urlParams.shopLocale,
          primary: false,
          machineTranslatable: false,
          machineTranslated: false,
          name: urlParams.shopLocale,
          published: false,
        };
      }

      if (currentLocaleInStorage) {
        return currentLocaleInStorage;
      }

      return undefined;
    })(),
  );

  const initialMarket = useRef<Market | undefined>(
    urlParams.market
      ? {
          id: '',
          name: decodeURIComponent(urlParams.market),
          primary: false,
          enabled: false,
          webPresences: [],
          handle: decodeURIComponent(urlParams.market),
        }
      : undefined,
  );

  const [marketDetailsLoading, setMarketDetailsLoading] = useState(false);
  const [currentLocale, privateSetCurrentLocale] = useState<
    undefined | ShopLocale
  >(initialLocale.current);

  const [currentMarket, privateSetCurrentMarket] = useState<undefined | Market>(
    initialMarket.current,
  );

  const {
    data: {markets: graphqlMarkets = [], shopLocales: locales = []} = {},
    loading: loadingShopLocalesAndMarkets,
    error,
  } = useQuery(ShopLocalesAndMarketsQuery, {
    ssr: false,
  });

  const markets = graphqlMarkets.map((market) => ({
    ...market,
    webPresences: market.webPresences.map((webPresence) => ({
      ...webPresence,
      alternateLocales: webPresence.alternateLocales.map(
        (alternateLocale) => alternateLocale.locale,
      ),
      defaultLocale: webPresence.defaultLocale.locale,
    })),
  }));

  const primaryLocale = useMemo(
    () => locales.find(({primary}) => primary)!,
    [locales],
  );

  const getAndSetMarketDetails = useCallback(
    async (market: Market) => {
      setMarketDetailsLoading(true);

      const {data} = await apollo.query<
        MarketDetailsQueryData,
        MarketDetailsQueryData.Variables
      >({
        query: MarketDetailsQuery,
        variables: {
          id: market.id,
        },
      });

      privateSetCurrentMarket({
        ...market,
        currencySettings: data.market.currencySettings,
        regions: data.market.regions,
      } as Market);
      setMarketDetailsLoading(false);
    },
    [apollo],
  );

  const setLocaleAndMarket = useCallback(
    (localeToSet?: string, marketToSet?: string) => {
      const locale = (() => {
        if (localeToSet) {
          const tentativeLocale = locales.find(
            ({locale}) => locale === localeToSet,
          );

          if (tentativeLocale) return tentativeLocale;
        }

        if (currentLocale) {
          const tentativeLocale = locales.find(
            ({locale}) => locale === currentLocale.locale,
          );
          if (tentativeLocale) return tentativeLocale;
        }

        const tentativeAlternate = locales.find(({primary}) => !primary);
        if (tentativeAlternate) return tentativeAlternate;

        return primaryLocale;
      })();

      const {marketHandle: newMarketHandle} = marketHandleFromLocaleChange(
        markets,
        locale.locale,
        marketToSet,
      );

      let market = newMarketHandle
        ? markets.find(({handle}) => handle === newMarketHandle)
        : undefined;

      if (locale?.primary && !market && markets.length) {
        market = markets[0];
      }

      if (newMarketHandle !== marketToSet) {
        setUrlParams({
          shopLocale: locale?.locale,
          market: market?.handle,
        });
      }

      privateSetCurrentLocale(locale);
      privateSetCurrentMarket(market);

      if (market) {
        getAndSetMarketDetails(market);
      }

      if (locale) {
        storeCurrentLocale(locale);
      }
    },
    [
      markets,
      setUrlParams,
      currentLocale,
      locales,
      primaryLocale,
      getAndSetMarketDetails,
      storeCurrentLocale,
    ],
  );

  useEffect(() => {
    if (!initialized) return;

    setLocaleAndMarket(
      urlParams.shopLocale,
      urlParams.market ? decodeURIComponent(urlParams.market) : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, urlParams.market, urlParams.shopLocale]);

  // This handles the case where the user navigates to the app without
  // a locale in the URL. We want to set the locale to the current locale
  useEffect(() => {
    if (!initialized) return;
    if (urlParams.shopLocale || !currentLocale) return;

    setUrlParams({
      shopLocale: currentLocale.locale,
      market: currentMarket?.handle,
    });
  }, [
    initialized,
    urlParams.shopLocale,
    currentLocale,
    currentMarket?.handle,
    setUrlParams,
  ]);

  /**
   * Locale: Initialize the states on mount
   */
  useEffect(() => {
    if (initialized || loadingShopLocalesAndMarkets || !locales.length) return;

    setLocaleAndMarket(
      urlParams.shopLocale ||
        currentLocaleInStorage?.locale ||
        locales.find(({primary}) => !primary)?.locale ||
        locales.find(({primary}) => primary)?.locale,
      urlParams.market ? decodeURIComponent(urlParams.market) : undefined,
    );
    setInitialized(true);
  }, [
    initialized,
    loadingShopLocalesAndMarkets,
    locales,
    currentLocaleInStorage,
    setLocaleAndMarket,
    urlParams.market,
    urlParams.shopLocale,
    urlParams,
  ]);

  const contextValues = useMemo(
    () => ({
      primaryLocale,
      currentLocale,
      locales: locales || [],
      currentMarket,
      markets: markets || [],

      loading:
        !initialized || loadingShopLocalesAndMarkets || marketDetailsLoading,
      error,
    }),
    [
      primaryLocale,
      currentLocale,
      locales,
      currentMarket,
      markets,
      initialized,
      loadingShopLocalesAndMarkets,
      marketDetailsLoading,
      error,
    ],
  );

  return (
    <ShopLocalesAndMarketsContext.Provider value={contextValues}>
      {children}
    </ShopLocalesAndMarketsContext.Provider>
  );
}
